import isEqual from "lodash/isEqual";

interface IRuleFilters {
  query: string;
  languages: Set<string>;
  categories: Set<string>;
  technologies: Set<string>;
  vulnerabilities: Set<string>;
  severities: Set<string>;
  visibilities: Set<string>;
}
interface ConstructorParams {
  query?: string;
  languages?: Set<string>;
  categories?: Set<string>;
  technologies?: Set<string>;
  vulnerabilities?: Set<string>;
  visibilities?: Set<string>;
  severities?: Set<string>;
}

type filterNames =
  | "languages"
  | "categories"
  | "technologies"
  | "vulnerabilities"
  | "severities"
  | "visibilities";
export class RuleFilters implements IRuleFilters {
  query: string = "";
  readonly normalizedQuery: string = "";
  readonly languages: Set<string> = new Set();
  readonly categories: Set<string> = new Set();
  readonly technologies: Set<string> = new Set();
  readonly vulnerabilities: Set<string> = new Set();
  readonly severities: Set<string> = new Set();
  readonly visibilities: Set<string> = new Set();

  constructor({
    query,
    languages,
    categories,
    technologies,
    vulnerabilities,
    severities,
    visibilities,
  }: ConstructorParams) {
    if (query !== undefined) {
      this.query = query;
      this.normalizedQuery = query.toLowerCase();
    }
    languages && languages.forEach((item) => this.languages.add(item));
    categories && categories.forEach((item) => this.categories.add(item));
    technologies && technologies.forEach((item) => this.technologies.add(item));
    vulnerabilities &&
      vulnerabilities.forEach((item) => this.vulnerabilities.add(item));
    severities && severities.forEach((item) => this.severities.add(item));
    visibilities && visibilities.forEach((item) => this.visibilities.add(item));
  }

  toQueryString(availableFilters: RuleFilters): string {
    let params: { [key: string]: string } = {};
    if (this.query !== "") {
      params["q"] = this.query;
    }
    if (this.hasFilterSet("languages", availableFilters)) {
      params["lang"] = Array.from(this.languages).join(",");
    }
    if (this.hasFilterSet("severities", availableFilters)) {
      params["sev"] = Array.from(this.severities).join(",");
    }
    if (this.hasFilterSet("categories", availableFilters)) {
      params["cat"] = Array.from(this.categories).join(",");
    }
    if (this.hasFilterSet("technologies", availableFilters)) {
      params["tech"] = Array.from(this.technologies).join(",");
    }
    if (this.hasFilterSet("vulnerabilities", availableFilters)) {
      params["owasp"] = Array.from(this.vulnerabilities).join(",");
    }
    if (this.hasFilterSet("visibilities", availableFilters)) {
      params["visib"] = Array.from(this.visibilities).join(",");
    }
    return new URLSearchParams(params).toString();
  }

  hasFilterSet(filter: filterNames, availableFilters: RuleFilters) {
    return (
      this[filter] &&
      this[filter].size &&
      !isEqual(this[filter], availableFilters[filter])
    );
  }

  clearFilter(setName: filterNames, available: RuleFilters) {
    return new RuleFilters({
      query: this.query,
      languages: setName === "languages" ? available.languages : this.languages,
      categories:
        setName === "categories" ? available.categories : this.categories,
      severities:
        setName === "severities" ? available.severities : this.severities,
      technologies:
        setName === "technologies" ? available.technologies : this.technologies,
      vulnerabilities:
        setName === "vulnerabilities"
          ? available.vulnerabilities
          : this.vulnerabilities,
      visibilities:
        setName === "visibilities" ? available.visibilities : this.visibilities,
    });
  }

  toggle(setName: filterNames, member: string, available: RuleFilters) {
    // If all filters are selected, then no filters are selected
    let newSet = isEqual(this[setName], available[setName])
      ? new Set<string>()
      : this[setName];
    if (newSet.has(member)) {
      newSet.delete(member);
    } else {
      newSet.add(member);
    }

    // when all filters are removed, show all items again
    if (newSet.size === 0) newSet = available[setName];

    return new RuleFilters({
      query: this.query,
      languages: setName === "languages" ? newSet : this.languages,
      categories: setName === "categories" ? newSet : this.categories,
      severities: setName === "severities" ? newSet : this.severities,
      technologies: setName === "technologies" ? newSet : this.technologies,
      vulnerabilities:
        setName === "vulnerabilities" ? newSet : this.vulnerabilities,
      visibilities: setName === "visibilities" ? newSet : this.visibilities,
    });
  }

  setQuery(query: string) {
    return new RuleFilters({
      query: query,
      languages: this.languages,
      severities: this.severities,
      categories: this.categories,
      technologies: this.technologies,
      vulnerabilities: this.vulnerabilities,
      visibilities: this.visibilities,
    });
  }

  equals(otherFilters: RuleFilters) {
    return (
      this.query === otherFilters.query &&
      this.languages.keys() === otherFilters.languages.keys() &&
      this.categories.keys() === otherFilters.categories.keys() &&
      this.severities.keys() === otherFilters.severities.keys() &&
      this.vulnerabilities.keys() === otherFilters.vulnerabilities.keys() &&
      this.technologies.keys() === otherFilters.technologies.keys() &&
      this.visibilities.keys() === otherFilters.visibilities.keys()
    );
  }

  static fromQueryString(
    queryString: string,
    availableFilters: RuleFilters
  ): RuleFilters {
    const urlParams = new URLSearchParams(queryString);
    const query = urlParams.get("q") || undefined;
    const langParam = urlParams.get("lang");
    const sevParam = urlParams.get("sev");
    const catParam = urlParams.get("cat");
    const techParam = urlParams.get("tech");
    const owaspParam = urlParams.get("owasp");
    const visibParam = urlParams.get("visib");

    const languages =
      langParam !== null
        ? new Set(langParam.split(","))
        : availableFilters.languages;
    const severities =
      sevParam !== null
        ? new Set(sevParam.split(","))
        : availableFilters.severities;
    const categories =
      catParam !== null
        ? new Set(catParam.split(","))
        : availableFilters.categories;
    const technologies =
      techParam !== null
        ? new Set(techParam.split(","))
        : availableFilters.technologies;
    const vulnerabilities =
      owaspParam !== null
        ? new Set(owaspParam.split(","))
        : availableFilters.vulnerabilities;
    const visibilities =
      visibParam !== null
        ? new Set(visibParam.split(","))
        : availableFilters.visibilities;

    return new this({
      query: query,
      languages: languages,
      severities: severities,
      categories: categories,
      technologies: technologies,
      vulnerabilities: vulnerabilities,
      visibilities: visibilities,
    });
  }

  static fromAvailable(
    oldFilters: RuleFilters,
    availableFilters: RuleFilters
  ): RuleFilters {
    const languages =
      oldFilters.languages.size > 0
        ? oldFilters.languages
        : availableFilters.languages;
    const severities =
      oldFilters.severities.size > 0
        ? oldFilters.severities
        : availableFilters.severities;
    const categories =
      oldFilters.categories.size > 0
        ? oldFilters.categories
        : availableFilters.categories;
    const technologies =
      oldFilters.technologies.size > 0
        ? oldFilters.technologies
        : availableFilters.technologies;
    const vulnerabilities =
      oldFilters.vulnerabilities.size > 0
        ? oldFilters.vulnerabilities
        : availableFilters.vulnerabilities;
    const visibilities =
      oldFilters.visibilities.size > 0
        ? oldFilters.visibilities
        : availableFilters.visibilities;
    return new this({
      query: oldFilters.query,
      languages: languages,
      categories: categories,
      vulnerabilities: vulnerabilities,
      technologies: technologies,
      severities: severities,
      visibilities: visibilities,
    });
  }
}
